import { setToken } from "@/stores/slices/authSlice";
import { useEffect } from "react";
import { useAppDispatch } from "@/stores/hook";
import { useNavigate } from "react-router-dom";
import useVerify from "@/features/auth/api/useVerify";
import { resetRoutesForNewSession } from "@/hooks/api/useRouterData";
import { takeReturnTo } from "@/features/auth/routes/loginReturn";

const LOGIN_URL = import.meta.env.VITE_LOGIN_URL ?? "https://xale.in";

export default function AuthRedirect() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { verify } = useVerify({ isFetch: false });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let token = params.get("token");

    if (token) {
      token = decodeURIComponent(token);
      localStorage.setItem("xale_access_token", token);
      verify();
      dispatch(setToken(token));
      // Same reasoning as the password login: this token may belong to a
      // different tenant/user than whatever is cached in this tab.
      resetRoutesForNewSession();
      // A link opened while signed out in this tab (see loginReturn.tsx).
      navigate(takeReturnTo() ?? "/", { replace: true });
    } else {
      window.location.href = LOGIN_URL;
    }
  }, [dispatch]);

  // Define styles using your CSS variables
  const containerStyle = { backgroundColor: "var(--color-black-9)" };
  const dotStyle = { backgroundColor: "var(--color-green-6)" };
  const textStyle = { color: "var(--color-green-4)" };

  return (
    // 1. Full screen container using your custom background variable
    <div
      className="flex flex-col items-center justify-center min-h-screen text-white"
      style={containerStyle}
    >
      <div className="flex space-x-3">
        <div
          className="
            w-4 h-4
            rounded-full
            animate-pulse
            shadow-xl
          "
          style={dotStyle}
          aria-hidden="true"
        ></div>
        <div
          className="
            w-4 h-4
            rounded-full
            animate-pulse
            opacity-50
            shadow-xl
          "
          style={dotStyle} // Applying the dot color variable
          aria-hidden="true"
        ></div>

        {/* Dot 3: Further staggered effect using default opacity utilities */}
        <div
          className="
            w-4 h-4
            rounded-full
            animate-pulse
            opacity-25
            shadow-xl
          "
          style={dotStyle} // Applying the dot color variable
          aria-hidden="true"
        ></div>
      </div>

      {/* 3. Message */}
      <p
        className="mt-8 text-xl font-light tracking-wider"
        style={textStyle} // Applying the text color variable
      >
        Authenticating Secure Connection
      </p>
    </div>
  );
}
